import { TimeSlotPackage } from 'app/utils/common/models'
import { KTSVG } from 'app/utils/helpers'
import { minuteToTime } from 'app/utils/services'
import moment from 'moment'
import { Col, Row, Container } from 'react-bootstrap-v5'
import { useTranslation } from 'react-i18next'
import { DateTimeVisitType } from './type'
import { Languages, locale } from 'app/utils/common/constants'

const DateTimeVisit = (props: DateTimeVisitType) => {
  const {
    isLoading,
    isMulti,
    isEmpty,
    isDeliveryTime,
    lang,
    dateTimeOptions,
    onSelectTimeSlot,
    onClearSelected,
  } = props
  const { t } = useTranslation()

  const selected = isMulti ? dateTimeOptions?.filter((s) => !s.related) : dateTimeOptions

  const renderTimeRange = (start: number, end: number, visitReminderStart?: number, visitReminderEnd?: number) => {
    const startValue = isDeliveryTime && visitReminderStart != null ? visitReminderStart : start
    const endValue = isDeliveryTime && visitReminderEnd != null ? visitReminderEnd : end
    const endDisplayValue = endValue > 1440 ? endValue % 1440 : endValue
    const nextDayLabel = t('NEXT_DAY').toLocaleLowerCase(
      lang === Languages.ar ? 'ar' : 'en'
    )

    return (
      <span>
        {minuteToTime(startValue, true, true, lang)} - {minuteToTime(endDisplayValue, true, true, lang)}
        {endValue > 1440 && <span className='next-day-lower'> {nextDayLabel}</span>}
      </span>
    )
  }

  const renderTimeSlots = (
    date: Date,
    timeSlots: TimeSlotPackage[]
  ) => {
    if (timeSlots.length > 0) {
      const half = Math.ceil(timeSlots.length / 2)

      const leftSlots: (TimeSlotPackage & {
        rawIndex: number
      })[] = timeSlots
        .slice(0, half)
        .map((el, index) => ({
          ...el,
          rawIndex: index
        }))

      const rightSlots: (TimeSlotPackage & {
        rawIndex: number
      })[] = timeSlots
        .slice(half)
        .map((el, index) => ({
          ...el,
          rawIndex: half + index
        }))

      return (
        <div className='time-slot-list'>
          {/* LEFT COLUMN */}
          {leftSlots.length > 0 && (
            <div className='morning-shift--time-slot'>
              {leftSlots.map((e, index) => {
                const isMorning =
                  e.start >= 300 && e.start < 960

                return (
                  <div
                    className='slot-item--wrapper'
                    key={index}
                  >
                    <button
                      key={e.rawIndex}
                      type='button'
                      className={`time-slot-item ${!e.isAvailable
                        ? 'disabled'
                        : e.isSelected
                          ? 'active'
                          : ''
                        }`}
                      onClick={() =>
                        e.rawIndex !== undefined &&
                        e.isAvailable &&
                        onSelectTimeSlot(
                          date,
                          timeSlots[e.rawIndex]
                        )
                      }
                    >
                      {!e.isAvailable && (
                        <div className='disabled-line'>
                          <KTSVG
                            defaultColor={true}
                            path='/assets/images/icons/line.svg'
                          />
                        </div>
                      )}

                      <span className='shift-icon'>
                        <KTSVG
                          defaultColor={true}
                          path={
                            isMorning
                              ? '/assets/images/icons/morning.svg'
                              : '/assets/images/icons/evening.svg'
                          }
                        />
                      </span>

                      <span className='time-slot-content'>
                        {renderTimeRange(
                          e.start,
                          e.end,
                          e.visitReminderStart,
                          e.visitReminderEnd
                        )}
                      </span>
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* RIGHT COLUMN */}
          {rightSlots.length > 0 && (
            <div
              className={`${lang !== Languages.ar
                ? 'evening-shift--time-slot'
                : 'evening-shift--time-slot isRtl'
                }`}
            >
              {rightSlots.map((e, index) => {
                const isMorning =
                  e.start >= 300 && e.start < 960

                return (
                  <div
                    className='slot-item--wrapper'
                    key={index}
                  >
                    <button
                      key={e.rawIndex}
                      type='button'
                      className={`time-slot-item ${!e.isAvailable
                        ? 'disabled'
                        : e.isSelected
                          ? 'active'
                          : ''
                        }`}
                      onClick={() =>
                        e.rawIndex !== undefined &&
                        e.isAvailable &&
                        onSelectTimeSlot(
                          date,
                          timeSlots[e.rawIndex]
                        )
                      }
                    >
                      {!e.isAvailable && (
                        <div className='disabled-line'>
                          <KTSVG
                            defaultColor={true}
                            path='/assets/images/icons/line.svg'
                          />
                        </div>
                      )}

                      <span className='shift-icon'>
                        <KTSVG
                          defaultColor={true}
                          path={
                            isMorning
                              ? '/assets/images/icons/morning.svg'
                              : '/assets/images/icons/evening.svg'
                          }
                        />
                      </span>

                      <span className='time-slot-content'>
                        {renderTimeRange(
                          e.start,
                          e.end,
                          e.visitReminderStart,
                          e.visitReminderEnd
                        )}
                      </span>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className='d-flex h-100 justify-content-center align-items-center p-3'>
        <span>
          {t(
            'CREATE_ORDER_DRAWER.SECTION_3.VALID.EMPTY_TIME_SLOT'
          )}
        </span>
      </div>
    )
  }

  return (
    <Container className='package-time-table'>
      <Row className='package-table-header'>
        <Col xs={3} className={`date-title ${lang === Languages.ar ? 'isRTL' : ''}`}>
          {t('CREATE_ORDER_DRAWER.SECTION_3.SELECTED_DATE')}
        </Col>
        <Col xs={9} className='time-title'>
          {t('CREATE_ORDER_DRAWER.SECTION_3.SELECTED_TIME')}
        </Col>
      </Row>
      <div className='package-table-body'>
        {isLoading ? (
          <div className='d-flex h-100 justify-content-center align-items-center'>
            <div className='spinner-border spinner-border-sm text-dark' role='status'></div>
          </div>
        ) : isEmpty ? (
          <div className='d-flex h-100 justify-content-center align-items-center p-3'>
            <span>{t('CREATE_ORDER_DRAWER.SECTION_3.VALID.EMPTY_TIME_SLOT')}</span>
          </div>
        ) : (
          <>
            {selected.map((datetime, index) => (
              <Row className='package-table-row'>
                <Col xs={3} className={`selected-item--wrapper ${lang === Languages.ar ? 'isRTL' : ''}`}>
                  <div className='selected-item' key={index}>
                    <div className='date-item'>
                      <div className={datetime.related ? 'item-wrapper--related' : 'item-wrapper'}>
                        <div className='item-selected'>
                          <span>
                            {datetime.date &&
                              moment(datetime.date).locale(locale.EN).format('MM/DD/YYYY')}
                          </span>
                          <div className='button-wrapper'>
                            {!isLoading && !datetime.related && (
                              <button
                                type='button'
                                onClick={() => onClearSelected(new Date(datetime.date))}
                              >
                                <i className='fa fa-times'></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={9} className={`timeslot-items--wrapper ${lang === Languages.ar ? 'isRTL' : ''}`}>
                  {datetime.timeSlot && renderTimeSlots(new Date(datetime.date), datetime.timeSlot)}
                </Col>
              </Row>
            ))}
          </>
        )}
      </div>
    </Container>
  )
}

export default DateTimeVisit
